import React from "react";
import moment from "moment-timezone";
import fieldPropTypes from "./field-prop-types";
import { MessagingContext } from "../context/messaging-layer";
import { fieldValidation as validation } from "../utils/field-validation";
import { convertToLocal, convertToUtc } from "../utils/date-time-helper";
import ValidationMessage from "../theme/default/validation-message";

const getTabTitle = (date, dateAndTime, placeholder, locale) => {
  let tabTitle = placeholder;
  if (date) {
    const dateFormat = "ddd, MMM D";
    tabTitle = moment(date).locale(locale).format(dateFormat);
  }

  if (dateAndTime) {
    const dateFormat = "ddd, MMM D h:mm A";
    tabTitle = moment(dateAndTime).locale(locale).format(dateFormat);
  }

  return tabTitle;
};

const changeHour = (date, hour) => {
  const changedDate = new Date(date);
  changedDate.setHours(hour, 0, 0);
  return changedDate;
};

const correctDate = (date, hour, flag) => {
  return flag ? changeHour(date, hour) : date;
};

class FormCalendarPicker extends React.Component {
  constructor(props) {
    super(props);

    const {
      field: {
        data: { start_date, end_date }
      },
      appData: {
        values: { time_zone }
      }
    } = props;

    this.state = {
      datesValues: null,
      timesValues: null,
      startDate: convertToLocal(start_date, time_zone),
      endDate: convertToLocal(end_date, time_zone),
      tabIndex: 0
    };

    this.datePickerRef = React.createRef();
    this.tabsRef = React.createRef();
    this.selectedTabRef = React.createRef();
  }

  componentDidMount() {
    const {
      field: {
        data: { selections },
        name
      },
      appData: { values, resetFieldValidation }
    } = this.props;
    const dates = {};
    selections.forEach(date => {
      dates[date.name] = values[date.name] || null;
    });

    this.setState({
      datesValues: { ...dates },
      timesValues: { ...dates }
    });

    resetFieldValidation(name);
  }

  componentDidUpdate() {
    const { field, appData } = this.props;
    const extraData = {
      fieldRef: this.tabsRef?.current ? this.tabsRef : this.datePickerRef,
      tabIndex: this.state.tabIndex
    };
    validation[field.type].update(field, appData, extraData);
  }

  handleDateChange = (date, name) => {
    const {
      field: { name: fieldName },
      appData: {
        resetFieldValidation,
        values: { time_zone }
      }
    } = this.props;

    this.setState(
      prevState => ({
        datesValues: {
          ...prevState.datesValues,
          [name]: convertToUtc(date, time_zone)
        },
        timesValues: {
          ...prevState.timesValues,
          [name]: null
        }
      }),
      () => this.handleDateChangeCallback(name)
    );

    resetFieldValidation(fieldName);
  };

  handleChangeTabIndex = () => {
    const {
      field: {
        data: { selections }
      }
    } = this.props;

    if (this.state.tabIndex + 1 < selections.length) {
      if (this.selectedTabRef.current) {
        this.selectedTabRef.current.children[this.state.tabIndex + 1].focus();
      }
      this.setState(prevState => ({
        tabIndex: prevState.tabIndex + 1
      }));
    }
  };

  handleTimeChange = (time, name) => {
    const {
      appData: {
        values: { time_zone }
      }
    } = this.props;

    this.setState(
      prevState => ({
        timesValues: {
          ...prevState.timesValues,
          [name]: convertToUtc(time, time_zone)
        }
      }),
      () => {
        this.handleDateChangeCallback(name);
      }
    );
  };

  handleDateChangeCallback = name => {
    const {
      appData: { handleChange }
    } = this.props;
    const { datesValues, timesValues, tabIndex } = this.state;

    if (datesValues[name]) {
      if (!timesValues[name]) {
        handleChange({
          [name]: null
        });
      } else {
        handleChange({
          [name]: timesValues[name]
        });
        if (!Object.values(datesValues)[tabIndex + 1]) {
          this.handleChangeTabIndex();
        }
      }
    }
  };

  isInvalid() {
    const { name } = this.props.field;
    const { fieldValidation } = this.props.appData;
    return fieldValidation[name] && !fieldValidation[name].isValid;
  }

  render() {
    const {
      field: {
        name,
        data: { selections, min_hour, max_hour, minute_increment = 30 }
      },
      appData: {
        values,
        fieldValidation,
        resetFieldValidation,
        isPulse,
        isEnhancedRedesign
      },
      components,
      appData
    } = this.props;
    const { time_zone } = values;
    const { Tabs, DatePicker, TimePicker, Label } = components;
    const { datesValues, timesValues, tabIndex, startDate, endDate } =
      this.state;
    const selection = selections[tabIndex];
    const dateValue = convertToLocal(datesValues?.[selection.name], time_zone);
    const timeValue = convertToLocal(timesValues?.[selection.name], time_zone);
    const invalid = this.isInvalid();
    const { selectionsState = {} } = fieldValidation[name] ?? {};
    const { hasErrorStatus, selectionErrorMessage } =
      selectionsState[selection.name] ?? {};

    // TODO: remove const below after adding all asterisks to required fields on be
    const isRequired =
      this.props.appData.showAsterisk &&
      !this.props.field.params.includes("IsOptional");

    const minTime = !moment(dateValue).isSame(startDate, "day")
      ? changeHour(dateValue, min_hour)
      : correctDate(startDate, min_hour, startDate.getHours() < min_hour);

    const maxTime = !moment(dateValue).isSame(endDate, "day")
      ? changeHour(dateValue, max_hour)
      : correctDate(endDate, max_hour, endDate.getHours() >= max_hour);

    const invalidDatePicker = !dateValue && invalid;
    const usePulseStyles = isPulse || isEnhancedRedesign;

    const exceptTimes =
      timesValues &&
      Object.keys(timesValues)
        .map(key => convertToLocal(timesValues[key], time_zone))
        .filter((_, i) => i !== tabIndex);
    const { visit_date_with_asterix: calendarTitle } =
      appData.settings.translations;

    const dateValueFormated =
      appData.locale &&
      moment(dateValue)?.locale &&
      moment(dateValue)?.locale(appData.locale).format
        ? moment(dateValue)?.locale(appData.locale).format("MMM D, YYYY")
        : moment(dateValue).format("MMM D, YYYY");

    return (
      <>
        {selections?.length > 1 && (
          <div ref={this.tabsRef}>
            <Tabs
              titles={selections.map(({ name: nameItem, placeholder }) => {
                // TODO: remove const below after adding all asterisks to required fields on be
                const hardcodedPlaceholder =
                  !placeholder?.includes("*") && isRequired
                    ? `${placeholder}*`
                    : placeholder;

                return getTabTitle(
                  convertToLocal(datesValues?.[nameItem], time_zone),
                  convertToLocal(timesValues?.[nameItem], time_zone),
                  // TODO: put placeholder variable instead value below
                  hardcodedPlaceholder,
                  appData.locale
                );
              })}
              selected={tabIndex}
              onChange={index => {
                this.setState({ tabIndex: index });
                resetFieldValidation(name);
              }}
              invalid={invalid}
            />
          </div>
        )}
        <div className='field-wrapper-web-sdk' ref={this.datePickerRef}>
          <div className='form-calendar-wrapper-web-sdk'>
            <Label
              name={name}
              text={calendarTitle}
              invalid={invalidDatePicker}
            />
            <DatePicker
              key={tabIndex}
              inline
              value={dateValue}
              minDate={startDate}
              maxDate={
                endDate.getHours() > min_hour ||
                (endDate.getHours() === min_hour &&
                  endDate.getMinutes() >= minute_increment)
                  ? endDate
                  : moment(endDate).subtract(1, "days").toDate()
              }
              onChange={date => this.handleDateChange(date, selection.name)}
              invalid={invalidDatePicker}
              components={components}
              usePulseStyles={usePulseStyles}
              appData={appData}
            />
          </div>

          {dateValue && (
            <div className='form-calendar-wrapper-web-sdk'>
              <TimePicker
                inline
                defaultValue={timeValue}
                minTime={minTime}
                maxTime={maxTime}
                step={minute_increment}
                onChange={time => {
                  resetFieldValidation(name);
                  this.handleTimeChange(time, selection.name);
                }}
                components={components}
                usePulseStyles={usePulseStyles}
                invalid={hasErrorStatus}
                dateValue={dateValueFormated}
                translations={this.props.appData?.settings?.translations}
                excludeTimes={exceptTimes}
                appData={appData}
              />
            </div>
          )}
          <ValidationMessage isInvalid={invalid}>
            {selectionErrorMessage}
          </ValidationMessage>
        </div>
      </>
    );
  }
}

FormCalendarPicker.propTypes = fieldPropTypes;
FormCalendarPicker.contextType = MessagingContext;

export default FormCalendarPicker;
